import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import axios from 'axios';

const Services = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/services', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // API возвращает объект с массивом services
        const servicesData = response.data.services || [];
        console.log('Полученные услуги:', servicesData);
        
        // Проверяем цены для отладки
        servicesData.forEach(service => {
          console.log(`Услуга ${service.name}:`, {
            priceType: service.priceType,
            basePrice: service.basePrice,
            hourlyPrice: service.hourlyPrice,
            dailyPrice: service.dailyPrice
          });
        });
        
        setServices(servicesData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching services:', error);
        setError('Ошибка при загрузке услуг');
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Filter services based on search query
  const filteredServices = services.filter((service) => {
    const searchStr = searchQuery.toLowerCase();
    return (
      service.name.toLowerCase().includes(searchStr) ||
      (service.description && service.description.toLowerCase().includes(searchStr))
    );
  });

  // Calculate pagination
  const paginatedServices = filteredServices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Open delete dialog
  const handleOpenDeleteDialog = (service) => {
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setServiceToDelete(null);
  };

  // Handle service deletion
  const handleDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      setDeleteLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`/api/services/${serviceToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setServices(services.filter(s => s.id !== serviceToDelete.id));
      setDeleteLoading(false);
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting service:', error);
      setError('Ошибка при удалении услуги');
      setDeleteLoading(false);
    }
  };

  // Format price based on price type
  const formatPrice = (service) => {
    switch (service.priceType) {
      case 'fixed':
        return service.basePrice ? `ОТ ${service.basePrice} ₽` : 'Цена по запросу';
      case 'hourly':
        return service.hourlyPrice ? `ОТ ${service.hourlyPrice} ₽/час` : 'Цена по запросу';
      case 'daily':
        return service.dailyPrice ? `ОТ ${service.dailyPrice} ₽/день` : 'Цена по запросу';
      default:
        return service.basePrice ? `ОТ ${service.basePrice} ₽` : 'Цена по запросу';
    }
  };

  // Get price type label
  const getPriceTypeLabel = (priceType) => {
    switch (priceType) {
      case 'fixed':
        return 'Фиксированная';
      case 'hourly':
        return 'Почасовая';
      case 'daily':
        return 'Посуточная';
      default:
        return 'Фиксированная';
    }
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: 2
      }}>
        <Typography variant="h5" component="h1">
          Управление услугами
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Поиск услуг..."
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/admin/services/add"
          >
            Добавить услугу
          </Button>
        </Box>
      </Box>
      
      <Paper elevation={2} sx={{ width: '100%', overflow: 'hidden' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Название</TableCell>
                    <TableCell>Описание</TableCell>
                    <TableCell>Тип цены</TableCell>
                    <TableCell>Цена</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedServices.length > 0 ? (
                    paginatedServices.map((service) => (
                      <TableRow key={service.id}>
                        <TableCell>{service.name}</TableCell>
                        <TableCell>
                          {service.description ? 
                            (service.description.length > 50 ? 
                              `${service.description.substring(0, 50)}...` : 
                              service.description) : 
                            ''}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getPriceTypeLabel(service.priceType)} 
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatPrice(service)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={service.isActive ? 'Активна' : 'Неактивна'} 
                            size="small"
                            color={service.isActive ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            component={RouterLink} 
                            to={`/services/${service.slug}`}
                            color="info"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton 
                            component={RouterLink} 
                            to={`/admin/services/edit/${service.id}`}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error"
                            onClick={() => handleOpenDeleteDialog(service)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {searchQuery 
                          ? 'Нет услуг, соответствующих поисковому запросу' 
                          : 'Нет доступных услуг'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredServices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Строк на странице:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить услугу "{serviceToDelete?.name}"? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button 
            onClick={handleDeleteService} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} />}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;
